const review = {
    key: "review",
    title: "Η εγγραφή χρειάζεται έλεγχο",
    description: "Βρέθηκε πρόβλημα στα αποθηκευμένα στοιχεία. Επικοινωνήστε με τον υπεύθυνο της πλατφόρμας για έλεγχο και διόρθωση.",
};

const days = (value) => Number.isFinite(value) && value > 0 ? value : null;

const predictionExplanation = (calculation) => {
    if (calculation.usableTests < 2) {
        return "Για την πρόβλεψη της τυπικής παραγωγής χρειάζονται τουλάχιστον δύο έλεγχοι με ποσότητα γάλακτος που να μπορεί να χρησιμοποιηθεί.";
    }
    const key = calculation.predictionKey;
    if (key) {
        const category = (value, max) => Number.isInteger(value) && value >= 1 && value <= max;
        if (!category(key.No_Lactation, 3) || !category(key.Prolificacy, 3)) {
            return "Ελέγξτε ποιος τοκετός είναι για το ζώο και πόσα μικρά γεννήθηκαν, μαζί με όσα γεννήθηκαν νεκρά. Η τυπική παραγωγή δεν υπολογίστηκε.";
        }
        if (category(key.QCL, 4) && category(key.Month, 3)) {
            return "Δεν υπάρχει συντελεστής πρόβλεψης για αυτή την περίπτωση. Η τυπική παραγωγή δεν υπολογίστηκε.";
        }
    }
    return "Η τυπική παραγωγή δεν υπολογίστηκε. Δεν υπάρχουν αρκετά στοιχεία για να εμφανιστεί η αιτία.";
};

export const formatMilkCalculationIssue = (issue, calculation = {}) => {
    const first = days(calculation.firstTestMax);
    const gap = days(calculation.maxIntervalBetweenChecks);
    const suckling = days(calculation.sucklingDays);
    const code = typeof issue === "string" ? issue : issue.code;
    const messages = {
        LEGACY_STATUS_1: {
            key: "late-first",
            title: first ? `Παραβίαση ορίου ${first} ημερών` : "Παραβίαση ορίου πρώτου ελέγχου",
            description: first
                ? `Ο πρώτος έλεγχος έγινε πάνω από ${first} ημέρες μετά τον τοκετό. Δεν υπολογίστηκε το συνολικό γάλα.`
                : "Ο πρώτος έλεγχος έγινε μετά το όριο της φυλής. Δεν υπολογίστηκε το συνολικό γάλα.",
        },
        LEGACY_STATUS_2: {
            key: "repeated-gaps",
            title: gap ? `Δύο διαστήματα πάνω από ${gap} ημέρες` : "Δύο μεγάλα διαστήματα χωρίς έλεγχο",
            description: gap
                ? `Σε δύο διαστήματα πέρασαν πάνω από ${gap} ημέρες χωρίς έλεγχο. Το γάλα υπολογίστηκε μόνο μέχρι τον προηγούμενο έλεγχο.`
                : "Σε δύο διαστήματα πέρασαν περισσότερες ημέρες από το όριο της φυλής χωρίς έλεγχο. Το γάλα υπολογίστηκε μόνο μέχρι τον προηγούμενο έλεγχο.",
        },
        LEGACY_STATUS_3: {
            key: "extreme-gap",
            title: gap ? `Διάστημα πάνω από ${2 * gap} ημέρες` : "Πολύ μεγάλο διάστημα χωρίς έλεγχο",
            description: gap
                ? `Πέρασαν πάνω από ${2 * gap} ημέρες ανάμεσα σε δύο ελέγχους. Το γάλα υπολογίστηκε μόνο μέχρι τον προηγούμενο έλεγχο.`
                : "Πέρασαν πάνω από το διπλάσιο του ορίου της φυλής ανάμεσα σε δύο ελέγχους. Το γάλα υπολογίστηκε μόνο μέχρι τον προηγούμενο έλεγχο.",
        },
        LEGACY_STATUS_4: {
            key: "no-tests",
            title: suckling ? `Χωρίς μέτρηση γάλακτος μετά τις ${suckling} ημέρες` : "Χωρίς μέτρηση γάλακτος μετά τον θηλασμό",
            description: suckling
                ? `Δεν βρέθηκε μέτρηση με ποσότητα γάλακτος που να μπορεί να χρησιμοποιηθεί μετά τις ${suckling} ημέρες από τον τοκετό. Δεν υπολογίστηκε το συνολικό γάλα.`
                : "Δεν βρέθηκε μέτρηση με ποσότητα γάλακτος που να μπορεί να χρησιμοποιηθεί μετά τον θηλασμό. Δεν υπολογίστηκε το συνολικό γάλα.",
        },
        MISSING_DAILY_INPUTS: {
            key: "daily-inputs",
            title: "Λείπουν ώρες ή ποσότητες γάλακτος",
            description: "Ελέγξτε τις ώρες όλων των καθημερινών αρμεγμάτων και τις ποσότητες του ζώου για όσα αρμέγματα μετρήθηκαν. Ο συγκεκριμένος έλεγχος δεν χρησιμοποιήθηκε στον υπολογισμό.",
        },
        INCOMPLETE_MEASUREMENTS: {
            key: "pending-measurements",
            title: "Εκκρεμούν ποσότητες μετά την αλλαγή αρμεγμάτων",
            description: "Άλλαξαν τα αρμέγματα που μετρήθηκαν. Συμπληρώστε τις ποσότητες του ζώου για όλα τα επιλεγμένα αρμέγματα και κάντε ξανά τον υπολογισμό. Μέχρι τότε δεν υπολογίζεται το συνολικό γάλα.",
        },
        MISSING_PREDICTION_INPUTS: {
            key: "prediction",
            title: "Δεν έγινε πρόβλεψη τυπικής παραγωγής",
            description: predictionExplanation(calculation),
        },
    };
    return { ...(messages[code] || review), testDate: issue.testDate };
};

export const getMilkCalculationProblems = (period) => {
    const calculation = period.calculation || {};
    const issues = [...(calculation.issues || [])];
    if ([1, 2, 3, 4].includes(period.calcCode)) issues.push({ code: `LEGACY_STATUS_${period.calcCode}` });
    const problems = issues
        .filter((issue) => (issue.code || issue) !== "MISSING_PREDICTION_INPUTS" || period.standardMilk == null)
        .map((issue) => formatMilkCalculationIssue(issue, calculation));
    if (period.milk == null && problems.length === 0) problems.push({ ...review });
    if (period.milk != null && period.standardMilk == null && problems.length === 0) {
        problems.push(formatMilkCalculationIssue({ code: "MISSING_PREDICTION_INPUTS" }, calculation));
    }
    const unique = new Map();
    problems.forEach((problem) => unique.set(`${problem.key}-${problem.testDate || ""}`, problem));
    return [...unique.values()];
};

export const milkProblemExplanation = (problem) => {
    const date = problem.testDate && new Date(problem.testDate);
    return date && !Number.isNaN(date.getTime())
        ? `${problem.description} Έλεγχος: ${date.toLocaleDateString("el-GR")}.`
        : problem.description;
};

export const getMilkCalculationIssueRows = (periods, issues = []) => {
    const pair = (entry) => `${entry.animalId}-${entry.lambingId}`;
    const periodKeys = new Set(periods.map(pair));
    const rows = periods.flatMap((period) => getMilkCalculationProblems(period).map((problem) => ({
        ...problem, flockCode: period.flockCode, registrationCode: period.registrationCode,
        id: `${pair(period)}-${problem.key}-${problem.testDate || ""}`,
    })));
    issues.filter((issue) => !periodKeys.has(pair(issue))).forEach((issue) => {
        const problem = formatMilkCalculationIssue(issue, issue.calculation);
        rows.push({ ...problem, flockCode: issue.flockCode, registrationCode: issue.registrationCode,
            id: `${pair(issue)}-${problem.key}-${problem.testDate || ""}` });
    });
    return [...new Map(rows.map((row) => [row.id, row])).values()];
};
